// Sends frontend log events to /api/log, which forwards them to Seq server-side
// (the browser never sees the Seq API key). Best-effort — never throws.

type ClientLogLevel = 'Information' | 'Warning' | 'Error';

function send(level: ClientLogLevel, message: string, properties?: Record<string, unknown>) {
  try {
    const body = JSON.stringify({ level, message, properties });
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/log', new Blob([body], { type: 'application/json' }));
      return;
    }
    fetch('/api/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // logging must never break the app
  }
}

export const logClientInfo = (message: string, properties?: Record<string, unknown>) =>
  send('Information', message, properties);

export const logClientWarning = (message: string, properties?: Record<string, unknown>) =>
  send('Warning', message, properties);

export const logClientError = (
  message: string,
  error?: unknown,
  properties?: Record<string, unknown>,
) =>
  send('Error', message, {
    ...properties,
    stack: error instanceof Error ? error.stack : undefined,
    errorMessage: error instanceof Error ? error.message : error ? String(error) : undefined,
  });

let installed = false;

// Captures uncaught errors and unhandled promise rejections anywhere on the page.
// Call once, e.g. from entry.client.tsx after hydration.
export function installGlobalErrorLogging() {
  if (installed) return;
  installed = true;

  window.addEventListener('error', (event) => {
    logClientError('Uncaught error', event.error ?? event.message, {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    logClientError('Unhandled promise rejection', event.reason);
  });
}
